import apiClient from '../lib/api-client';
import { getAttemptToken, setAttemptToken } from '../lib/attempt-token';

/**
 * Headers carrying the anonymous attempt token, when one is stored for the
 * attempt. Authenticated attempts have no token and send nothing.
 */
function attemptHeaders(attemptId: string): {
  headers?: Record<string, string>;
} {
  const token = getAttemptToken(attemptId);
  return token ? { headers: { 'x-attempt-token': token } } : {};
}

export interface Exam {
  _id: string;
  title: string;
  description: string;
  type: 'reading' | 'listening' | 'speaking' | 'writing' | 'knm';
  level?: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  duration: number;
  questionCount?: number;
  requiresSubscription?: boolean;
  allowedPlans?: string[];
  isTrial?: boolean;
}

export interface StartExamResponse {
  attemptId: string;
  examId: string;
  startTime: string;
  expiresAt: string;
  status: string;
  savedAnswers: number;
  /** Present only for anonymous attempts; required on every later request. */
  anonymousToken?: string | null;
}

export interface ResumeAttemptResponse {
  attemptId: string;
  exam: {
    _id: string;
    title: string;
    type: string;
    level?: string;
    duration: number;
    passingScore?: number;
    questions: Array<{
      _id: string;
      text: string;
      prompt?: string;
      passageTitle?: string;
      theme?: string;
      type: string;
      options: Array<{ text: string }>;
      points?: number;
      context?: string;
      minWords?: number;
      maxWords?: number;
      media?: { audio?: string; image?: string; video?: string };
      playsAllowed?: number;
      prepTime?: number;
      speakTime?: number;
    }>;
  };
  startTime: string;
  expiresAt: string;
  status: string;
  savedAnswers: Array<{ questionId: string; answer: unknown; timeSpent?: number }>;
  remainingTime: number;
}

export const examService = {
  async getExams(params?: { type?: string; level?: string }) {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.level) query.set('level', params.level);
    const url = query.toString() ? `/exams?${query.toString()}` : '/exams';
    const response = await apiClient.get<Exam[]>(url);
    return response.data;
  },

  async getExamById(id: string) {
    const response = await apiClient.get<Exam>(`/exams/${id}`);
    return response.data;
  },

  async startExam(examId: string) {
    const response = await apiClient.post<StartExamResponse>(`/exams/${examId}/start`);
    const data = response.data;
    // Persist the anonymous token next to the attempt so a refresh keeps access.
    if (data?.anonymousToken && data?.attemptId) {
      setAttemptToken(data.attemptId, data.anonymousToken);
    }
    return data;
  },

  async resumeAttempt(attemptId: string) {
    const response = await apiClient.get<ResumeAttemptResponse>(
      `/exams/attempts/${attemptId}/resume`,
      attemptHeaders(attemptId),
    );
    return response.data;
  },

  async saveAnswer(
    attemptId: string,
    questionId: string,
    answer: unknown,
    timeSpent?: number,
  ) {
    await apiClient.post(
      `/exams/attempts/${attemptId}/answer`,
      {
        questionId,
        answer,
        ...(timeSpent != null && { timeSpent }),
      },
      attemptHeaders(attemptId),
    );
  },

  async submitExam(attemptId: string) {
    const response = await apiClient.post<{
      attemptId: string;
      status: string;
      gradingStatus?: 'not-required' | 'pending' | 'processing' | 'completed' | 'failed';
      score: number | null;
      percentage: number | null;
      passed: boolean | null;
      submittedAt: string;
      totalAnswers: number;
    }>(`/exams/attempts/${attemptId}/submit`, undefined, attemptHeaders(attemptId));
    return response.data;
  },

  async retrySpeakingGrading(attemptId: string) {
    const response = await apiClient.post<{ attemptId: string; status: string }>(
      `/exams/attempts/${attemptId}/grade-speaking`,
      undefined,
      attemptHeaders(attemptId),
    );
    return response.data;
  },

  async gradeWritingAttempt(attemptId: string) {
    const response = await apiClient.post(
      `/exams/attempts/${attemptId}/grade`,
      undefined,
      attemptHeaders(attemptId),
    );
    return response.data;
  },

  async uploadAttemptAudio(attemptId: string, questionId: string, audioBlob: Blob): Promise<{ url: string }> {
    const form = new FormData();
    const mime = audioBlob.type.toLowerCase();
    const extension = mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : mime.includes('wav') ? 'wav' : 'webm';
    form.append('audio', audioBlob, `recording.${extension}`);
    form.append('questionId', questionId);
    const response = await apiClient.post<{ url: string }>(
      `/exams/attempts/${attemptId}/audio`,
      form,
      attemptHeaders(attemptId),
    );
    return response.data;
  },
};
